import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import _delay from 'lodash.delay'
import styled, { keyframes } from 'styled-components'
import { CircularProgress } from '@material-ui/core'
import { useEventCountdown } from '../../hooks/use-countdown'
import { useEventById } from '../../hooks/use-event-by-id'
import { WebRtcPlayer } from './WebRtcPlayer'
import { HlsPlayer } from './HlsPlayer'
import { PLAYBACK_PROTOCOLS, resolvePlaybackProtocol } from '../../utils/playbackProtocol'
import { selectCurrentStore } from '../../reducers/storeSlice'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'

const childFade = keyframes`
  from { opacity: 0; transform: scale(1.01); }
  to { opacity: 1; transform: scale(1); }
`

const Paragraph = styled.p`
  font-size: 1rem;
  text-shadow: 0px 0px 6px rgba(0, 0, 0, 0.6);
  color: ${({ color }) => color};
  font-weight: 600;
`

const StreamingContainer = styled.div`
  /* El contenedor se mantiene SIEMPRE en el árbol de layout. Con \`display: none\`
     el <video> no genera caja y por lo tanto no se compone, así que
     \`requestVideoFrameCallback\` nunca dispara — justo el escalón que la app usa
     para saber si WebRTC presentó un frame. Se oculta con opacidad y se saca
     del flujo con posicionamiento absoluto para que el spinner ocupe su lugar. */
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  height: 100%;
  width: 100%;
  opacity: ${({ streamingInProgress }) => (streamingInProgress ? 1 : 0)};
  filter: ${({ streamingInProgress, blurStreaming }) => {
    if (!streamingInProgress) return 'blur(6px)'
    return blurStreaming ? 'blur(4px)' : 'blur(0)'
  }};
  transition: opacity 1100ms ease, filter 1300ms ease;
  will-change: opacity, filter;
  pointer-events: ${({ streamingInProgress }) => (streamingInProgress ? 'auto' : 'none')};

  & > * {
    animation: ${childFade} 1000ms ease;
  }
`
const SpinnerContainer = styled.div`
  /* Sin \`z-index\`: alcanza con venir después de StreamingContainer en el DOM para
     quedar por encima del video. Ponerle \`z-index: 1\` lo empataba con
     \`BaseCallLayout\` (que también es 1) y, al estar más abajo en el DOM, terminaba
     pintando sobre el header, el chat y el botón de cerrar. En el iframe inyectable
     y en los webviews de las apps eso deja al usuario sin forma de cerrar el
     widget mientras espera la transmisión. */
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  /* Un spinner nunca debe capturar clicks. */
  pointer-events: none;
`

const VideosContainer = styled.div`
  position: relative;
  background-image: ${({ showSpinner }) =>
    showSpinner === true
      ? `url('https://d3rl3e7cakfevs.cloudfront.net/tencent/liveshopping-shield.png')`
      : `none`};
  background-size: auto 100%;
  background-position: center;
  background-repeat: no-repeat;
  height: 100%;
  filter: ${({ blurStreaming }) => (blurStreaming ? 'blur(4px)' : 'none')};
`

const Streaming = () => {
  const { t } = useTranslation()
  const [streamingInProgress, setStreamingInProgress] = useState(false)
  const [showSpinner, setShowSpinner] = useState(true)
  const { data: event } = useEventById()
  const { isActive: eventIsStarting } = useEventCountdown(event.secondsUntilStart)
  const { isActive: eventIsFinishing } = useEventCountdown(event.secondsUntilFinish)
  const [searchParams] = useSearchParams()
  const isDemo = searchParams.get('demo') === 'true'
  const store = useSelector(selectCurrentStore)
  const playbackProtocol = resolvePlaybackProtocol(store)
  const blurStreaming = (eventIsStarting || eventIsFinishing) && !isDemo

  useEffect(() => {
    if (!streamingInProgress && showSpinner) {
      /**
       * Cuánto se muestra el spinner antes de pasar al texto «No hay una transmisión en
       * curso». Es el escalón CARO: el spinner dice «esperá», el texto dice «no hay nada».
       *
       * Se subió de 30 s a 80 s junto con la bajada de la ventana de freeze de 60 s a 12 s
       * en los dos hooks de reproducción. Los dos cambios se compensan a propósito: el
       * espectador deja de ver la imagen vieja a los ~12 s en vez de ~60, y el texto sigue
       * apareciendo alrededor de los 90 s como antes. Si se toca uno hay que tocar el otro.
       */
      const timer = _delay(() => {
        setShowSpinner(false)
      }, 80000)

      return () => clearTimeout(timer)
    }
  }, [showSpinner, streamingInProgress])

  useEffect(() => {
    if (!streamingInProgress) {
      setShowSpinner(true)
    }
  }, [streamingInProgress])

  return (
    <VideosContainer showSpinner={showSpinner} blurStreaming={blurStreaming}>
      <StreamingContainer
        id="stream-container"
        blurStreaming={blurStreaming}
        streamingInProgress={streamingInProgress}
        data-active={streamingInProgress ? 'true' : 'false'}
      >
        {/* Un camino o el otro, nunca los dos: cada uno trae su propio <video> y su
            propia lógica. La decisión se toma acá una sola vez. */}
        {playbackProtocol === PLAYBACK_PROTOCOLS.HLS ? (
          <HlsPlayer
            setStreamingInProgress={setStreamingInProgress}
            streamingInProgress={streamingInProgress}
          />
        ) : (
          <WebRtcPlayer
            setStreamingInProgress={setStreamingInProgress}
            streamingInProgress={streamingInProgress}
          />
        )}
      </StreamingContainer>

      {!streamingInProgress && (
        <SpinnerContainer>
          {showSpinner === true ? (
            <CircularProgress color="secondary" size="2.5rem" />
          ) : (
            <Paragraph color="white">{t('uiElements.noStreamingParagraph')}</Paragraph>
          )}
        </SpinnerContainer>
      )}
    </VideosContainer>
  )
}

Streaming.propTypes = {
  children: PropTypes.element,
}

export { Streaming }
